// ConfirmModal — reusable destructive-action confirmation dialog (A04/A07/A09/
// A13/A14 patterns). Per sub-plan 14: shown before delete, reset-password,
// broadcast send, permission change.

import type { ReactNode } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { tokens } from "@findgeeks/ui-tokens";

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Use danger color for the confirm button (delete/destructive). */
  danger?: boolean;
  /** Disable confirm while async work is in flight. */
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  pending = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  return (
    <Dialog open={open} onClose={pending ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: tokens.font.sizes.h3 }}>{title}</DialogTitle>
      <DialogContent>
        {typeof body === "string" ? (
          <DialogContentText>{body}</DialogContentText>
        ) : (
          body
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={pending} color="inherit">
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={pending}
          variant="contained"
          sx={{
            background: danger
              ? tokens.colors.admin.danger
              : tokens.colors.admin.primary,
            "&:hover": {
              background: danger
                ? tokens.colors.admin.danger
                : tokens.colors.admin.primary,
            },
          }}
        >
          {pending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
